import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";

function OnlineExamResultPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [examId, setExamId] = useState("");
  const [examOptions, setExamOptions] = useState([]);
  const [franchiseOptions, setFranchiseOptions] = useState([]);
  const [franchiseId, setFranchiseId] = useState("");
  const [franchiseSearch, setFranchiseSearch] = useState("");
  const [appliedFranchiseSearch, setAppliedFranchiseSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState("10");
  const [meta, setMeta] = useState({ page: 1, per_page: 10, total: 0, last_page: 1 });

  const loadExams = async () => {
    try {
      const [examRes, franchiseRes] = await Promise.all([
        axios.get(`${API_BASE}/exams-admin`),
        axios.get(`${API_BASE}/franchises`),
      ]);
      setExamOptions(Array.isArray(examRes.data) ? examRes.data : []);
      setFranchiseOptions(Array.isArray(franchiseRes.data) ? franchiseRes.data : []);
    } catch (error) {
      setExamOptions([]);
      setFranchiseOptions([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/exams-admin/results`, {
        params: {
          page,
          per_page: perPage,
          search: search || undefined,
          exam_id: examId || undefined,
          franchise_id: franchiseId || undefined,
        },
      });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setMeta(res.data?.meta || { page: 1, per_page: 10, total: 0, last_page: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExams();
  }, []);

  useEffect(() => {
    load();
  }, [page, perPage, examId, franchiseId]);

  const handleSearch = () => {
    setPage(1);
    load();
  };

  const resetFilters = () => {
    setSearch("");
    setExamId("");
    setFranchiseId("");
    setFranchiseSearch("");
    setAppliedFranchiseSearch("");
    setPerPage("10");
    setPage(1);
  };

  const filteredFranchiseOptions = useMemo(() => {
    const needle = (appliedFranchiseSearch || "").trim().toLowerCase();
    if (!needle) return franchiseOptions;
    return franchiseOptions.filter((f) => {
      const label = `${f.centre_name || ""} ${f.login_id || ""} ${f.centre_code || ""}`.toLowerCase();
      return label.includes(needle);
    });
  }, [franchiseOptions, appliedFranchiseSearch]);

  const applyFranchiseSearch = () => {
    setAppliedFranchiseSearch(franchiseSearch);
  };

  const exportCsv = () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (examId) params.set("exam_id", examId);
    if (franchiseId) params.set("franchise_id", franchiseId);
    const qs = params.toString();
    const url = `${API_BASE}/exams-admin/results/export${qs ? `?${qs}` : ""}`;
    window.open(url, "_blank");
  };

  const pageInfo = useMemo(() => `Page ${meta.page || 1} / ${meta.last_page || 1}`, [meta.page, meta.last_page]);

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Online Exam Result</h3>
              <div className="badge bg-light text-dark border">Total: {Number(meta.total || 0)}</div>
            </div>

            <div className="row g-2 mb-3">
              <div className="col-lg-4">
                <input
                  className="form-control"
                  placeholder="Search student / exam / franchise / registration"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                />
              </div>
              <div className="col-lg-2">
                <select className="form-select" value={examId} onChange={(e) => { setExamId(e.target.value); setPage(1); }}>
                  <option value="">All Exam</option>
                  {examOptions.map((ex) => (
                    <option key={ex.id} value={ex.id}>{ex.title}</option>
                  ))}
                </select>
              </div>
              <div className="col-lg-3">
                <div className="input-group">
                  <input
                    className="form-control"
                    placeholder="Search Study Centre"
                    value={franchiseSearch}
                    onChange={(e) => setFranchiseSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") applyFranchiseSearch(); }}
                  />
                  <button className="btn btn-primary" type="button" onClick={applyFranchiseSearch}>Search</button>
                </div>
              </div>
              <div className="col-lg-2">
                <select className="form-select" value={franchiseId} onChange={(e) => { setFranchiseId(e.target.value); setPage(1); }}>
                  <option value="">All Study Centre</option>
                  {filteredFranchiseOptions.map((f) => (
                    <option key={f.id} value={f.id}>{f.centre_name || f.login_id || `Franchise ${f.id}`}</option>
                  ))}
                </select>
              </div>
              <div className="col-lg-2">
                <select className="form-select" value={perPage} onChange={(e) => { setPerPage(e.target.value); setPage(1); }}>
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">All</option>
                </select>
              </div>
              <div className="col-lg-3 d-flex gap-2 flex-wrap">
                <button className="btn btn-primary" onClick={handleSearch}>Filter</button>
                <button className="btn btn-outline-secondary" onClick={resetFilters}>Reset</button>
                <button className="btn btn-success" onClick={exportCsv}>Export CSV</button>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead>
                  <tr>
                    <th>Sl No</th>
                    <th>Exam Name</th>
                    <th>Exam Date</th>
                    <th>Exam Time</th>
                    <th>Student Name</th>
                    <th>Registration No</th>
                    <th>Franchise</th>
                    <th>Score</th>
                    <th>Total Questions</th>
                    <th>Total Marks</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="11">Loading...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan="11">No generated result found.</td></tr>
                  ) : rows.map((r, idx) => (
                    <tr key={r.id}>
                      <td>{perPage === "all" ? idx + 1 : ((Number(meta.page || 1) - 1) * Number(meta.per_page || 10)) + idx + 1}</td>
                      <td>{r.exam_title || "-"}</td>
                      <td>{r.exam_date || "-"}</td>
                      <td>{r.exam_time || "-"}</td>
                      <td>{r.student_name || "-"}</td>
                      <td>{r.registration_no || "-"}</td>
                      <td>{r.franchise_name || r.franchise_login_id || "-"}</td>
                      <td>{Number(r.score || 0)}</td>
                      <td>{Number(r.total_questions || 0)}</td>
                      <td>{Number(r.total_marks || 0)}</td>
                      <td><span className="badge bg-success">{r.result_status || "generated"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {perPage !== "all" ? (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                <div className="small text-muted">{pageInfo}</div>
                <div className="d-flex gap-2">
                  <button className="btn btn-sm btn-outline-secondary" disabled={Number(meta.page || 1) <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
                  <button className="btn btn-sm btn-outline-secondary" disabled={Number(meta.page || 1) >= Number(meta.last_page || 1)} onClick={() => setPage((p) => Math.min(Number(meta.last_page || 1), p + 1))}>Next</button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default OnlineExamResultPage;
